import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { formatMoney } from '@johnsaks/core'
import { useState } from 'react'
import { BtnGhost, BtnPrimary, Field, TextArea, TextInput } from '@/components/field'
import { EmptyState } from '@/components/empty-state'
import { Kpi } from '@/components/kpi'
import { Modal } from '@/components/modal'
import { RowActions } from '@/components/row-actions'
import { StatusPill } from '@/components/status-pill'
import { useToast } from '@/components/toast'
import { api, apiMutate, type InventoryRow } from '@/lib/api'

const TONE: Record<string, string> = {
  in: 'ok',
  low: 'warn',
  out: 'bad',
}

const GAUGE: Record<string, string> = {
  in: 'bg-vert-texte',
  low: 'bg-ambre-texte',
  out: 'bg-rouge',
}

function gaugePct(available: number, threshold: number) {
  const denom = Math.max(threshold * 2, available, 1)
  return Math.min(100, Math.round((available / denom) * 100))
}

export function Stocks() {
  const toast = useToast()
  const qc = useQueryClient()
  const [edit, setEdit] = useState<{
    productId: string
    name: string
    variantId: string
    label: string
    threshold: number
  } | null>(null)
  const [delta, setDelta] = useState('0')
  const [threshold, setThreshold] = useState('5')
  const [note, setNote] = useState('')

  const inv = useQuery({
    queryKey: ['admin-inventory'],
    queryFn: () => api<{ items: InventoryRow[] }>('/admin/inventory?limit=200'),
  })

  const adjust = useMutation({
    mutationFn: () =>
      apiMutate('/admin/inventory/adjustments', 'POST', {
        variantId: edit!.variantId,
        delta: Number.parseInt(delta, 10) || 0,
        threshold: Number.parseInt(threshold, 10) || 0,
        note: note || undefined,
      }),
    onSuccess: () => {
      toast.ok('Stock mis à jour')
      setEdit(null)
      void qc.invalidateQueries({ queryKey: ['admin-inventory'] })
      void qc.invalidateQueries({ queryKey: ['admin-badges'] })
      void qc.invalidateQueries({ queryKey: ['admin-dashboard'] })
    },
    onError: (e: Error) => toast.err(e.message),
  })

  if (inv.isError) {
    return (
      <div className="carte-admin p-6">
        <p className="text-rouge-texte">Impossible de charger l’inventaire.</p>
      </div>
    )
  }

  const items = inv.data?.items ?? []
  const variantRows = items.flatMap((p) =>
    (p.variants?.length ? p.variants : [{
      variantId: p.productId,
      sku: p.sku,
      label: 'Produit',
      color: '',
      active: true,
      isDefault: true,
      onHand: p.onHand,
      reserved: p.reserved,
      available: p.available,
      threshold: p.threshold,
      tone: p.tone,
      labelStatus: p.label,
      incoming: 0,
    }]).map((v) => ({ product: p, variant: v })),
  )
  const unites = variantRows.reduce((s, r) => s + r.variant.available, 0)
  const sousSeuil = variantRows.filter((r) => r.variant.tone !== 'in')
  const valeur = items.reduce((s, r) => s + r.priceCents * r.available, 0)

  function openEdit(product: InventoryRow, variantId: string, label: string, thr: number) {
    setEdit({ productId: product.productId, name: product.name, variantId, label, threshold: thr })
    setDelta('0')
    setThreshold(String(thr))
    setNote('')
  }

  return (
    <div className="flex flex-col gap-5">
      <section className="grid gap-4 sm:grid-cols-2 xl:grid-cols-4">
        <Kpi label="Produits" valeur={inv.isLoading ? '—' : String(items.length)} />
        <Kpi label="Variantes" valeur={inv.isLoading ? '—' : String(variantRows.length)} />
        <Kpi label="Unités dispo" valeur={inv.isLoading ? '—' : String(unites)} />
        <Kpi label="Sous le seuil" valeur={inv.isLoading ? '—' : String(sousSeuil.length)} />
      </section>

      {inv.isLoading ? (
        <p className="text-second-admin">Chargement…</p>
      ) : items.length === 0 ? (
        <EmptyState title="Inventaire vide" body="Aucun article en stock pour le moment." />
      ) : (
        <section className="carte-admin">
          <div className="flex items-center justify-between border-b border-bordure-admin px-4 py-3">
            <h2 className="text-base">Inventaire par variante</h2>
            <span className="kicker text-second-admin">{variantRows.length} lignes</span>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-left">
              <thead>
                <tr className="border-b border-bordure-admin">
                  {['Produit', 'Variante', 'SKU', 'En main', 'Réservé', 'Dispo', 'État', 'Jauge', ''].map(
                    (h) => (
                      <th key={h || 'a'} className="px-4 py-2.5">
                        {h}
                      </th>
                    ),
                  )}
                </tr>
              </thead>
              <tbody>
                {variantRows.map(({ product: p, variant: v }) => (
                  <tr
                    key={`${p.productId}-${v.variantId}`}
                    className="border-b border-bordure-admin last:border-0"
                  >
                    <td className="px-4 py-2.5">
                      <div className="flex items-center gap-3">
                        <span className="size-9 shrink-0 overflow-hidden rounded-champ bg-gris-fond">
                          {p.image ? (
                            <img src={p.image.src} alt="" className="size-full object-cover" />
                          ) : null}
                        </span>
                        <span className="font-medium text-encre-admin">{p.name}</span>
                      </div>
                    </td>
                    <td className="px-4 py-2.5">{v.label || v.color || '—'}</td>
                    <td className="px-4 py-2.5 text-second-admin">{v.sku}</td>
                    <td className="px-4 py-2.5 tabular-nums">{v.onHand}</td>
                    <td className="px-4 py-2.5 tabular-nums">{v.reserved}</td>
                    <td className="px-4 py-2.5 tabular-nums">{v.available}</td>
                    <td className="px-4 py-2.5">
                      <StatusPill
                        label={v.labelStatus ?? v.tone}
                        tone={TONE[v.tone] ?? 'neutral'}
                      />
                    </td>
                    <td className="px-4 py-2.5">
                      <div className="h-1.5 w-24 overflow-hidden rounded-full bg-gris-fond">
                        <div
                          className={`h-full rounded-full ${GAUGE[v.tone] ?? 'bg-encre-admin'}`}
                          style={{ width: `${gaugePct(v.available, v.threshold)}%` }}
                        />
                      </div>
                    </td>
                    <td className="px-4 py-2.5 text-right">
                      <RowActions
                        items={[
                          {
                            label: 'Ajuster',
                            onSelect: () =>
                              openEdit(p, v.variantId, v.label || v.color || v.sku, v.threshold),
                          },
                          {
                            label: 'Voir le produit',
                            onSelect: () => {
                              window.location.href = `/produits/${p.productId}`
                            },
                          },
                        ]}
                      />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <p className="border-t border-bordure-admin px-4 py-2 text-xs text-second-admin">
            Valeur indicative : {formatMoney(valeur)}
          </p>
        </section>
      )}

      <Modal
        open={Boolean(edit)}
        title={edit ? `Stock · ${edit.name} · ${edit.label}` : 'Stock'}
        onClose={() => setEdit(null)}
        footer={
          <>
            <BtnGhost onClick={() => setEdit(null)}>Annuler</BtnGhost>
            <BtnPrimary disabled={adjust.isPending} onClick={() => adjust.mutate()}>
              Enregistrer
            </BtnPrimary>
          </>
        }
      >
        <div className="flex flex-col gap-3">
          <Field label="Ajustement (+/−)">
            <TextInput
              type="number"
              value={delta}
              onChange={(e) => setDelta(e.target.value)}
            />
          </Field>
          <Field label="Seuil d’alerte">
            <TextInput
              type="number"
              min="0"
              value={threshold}
              onChange={(e) => setThreshold(e.target.value)}
            />
          </Field>
          <Field label="Note">
            <TextArea value={note} onChange={(e) => setNote(e.target.value)} rows={3} />
          </Field>
        </div>
      </Modal>
    </div>
  )
}
